import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Alert,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Divider,
} from '@mui/material';
import { Visibility as ViewIcon, Close as CloseIcon } from '@mui/icons-material';
import { format } from 'date-fns';
import api from '../config/api';
import {
  StyledTableContainer,
  TableSkeleton,
  FilterBar,
  FilterSelect,
  StyledPagination,
  EmptyState,
  StatusChip,
} from '../components/TableShared';

const ff = "'Poppins', sans-serif";

const SERVICE_TYPE_COLORS = {
  BOARDING: { color: '#3B82F6', bg: '#EFF6FF' },
  HOUSE_SITTING: { color: '#8B5CF6', bg: '#F5F3FF' },
  DROP_IN_VISITS: { color: '#0EA5E9', bg: '#F0F9FF' },
  DAY_CARE: { color: '#F59E0B', bg: '#FFFBEB' },
  PET_WALKING: { color: '#22C55E', bg: '#F0FDF4' },
};

const SERVICE_TYPE_LABELS = {
  BOARDING: 'Boarding',
  HOUSE_SITTING: 'House Sitting',
  DROP_IN_VISITS: 'Drop-In Visits',
  DAY_CARE: 'Day Care',
  PET_WALKING: 'Pet Walking',
};

const ALL_SERVICE_TYPES = [
  'BOARDING',
  'HOUSE_SITTING',
  'DROP_IN_VISITS',
  'DAY_CARE',
  'PET_WALKING',
];

function formatDateNice(dateString) {
  if (!dateString) return 'N/A';
  try {
    return format(new Date(dateString), 'MMM dd, yyyy hh:mm a');
  } catch {
    return 'N/A';
  }
}

export default function Services() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalServices, setTotalServices] = useState(0);
  const [serviceTypeFilter, setServiceTypeFilter] = useState('All');
  const [selectedService, setSelectedService] = useState(null);

  const fetchServices = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const params = {
        page: page + 1,
        limit: rowsPerPage,
      };
      if (serviceTypeFilter !== 'All') {
        params.serviceType = serviceTypeFilter;
      }
      const response = await api.get('/admin/services', { params });
      const data = response.data.data || response.data;
      setServices(data.services || []);
      setTotalServices(data.total || data.services?.length || 0);
    } catch (err) {
      console.error('Error fetching services:', err);
      setError('Failed to load services. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, serviceTypeFilter]);

  useEffect(() => {
    fetchServices();
  }, [fetchServices]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleServiceTypeFilterChange = (event) => {
    setServiceTypeFilter(event.target.value);
    setPage(0);
  };

  return (
    <Box>
      <FilterBar>
        <FilterSelect
          label="Service Type"
          value={serviceTypeFilter}
          onChange={handleServiceTypeFilterChange}
          minWidth={180}
          options={[
            { value: 'All', label: 'All' },
            ...ALL_SERVICE_TYPES.map((type) => ({
              value: type,
              label: SERVICE_TYPE_LABELS[type] || type,
            })),
          ]}
        />
      </FilterBar>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {loading ? (
        <TableSkeleton columns={5} rows={6} />
      ) : (
        <StyledTableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Sitter Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Service Type</TableCell>
                <TableCell>Completed</TableCell>
                <TableCell>Created Date</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {services.length === 0 ? (
                <EmptyState colSpan={6} message="No services found." />
              ) : (
                services.map((service) => (
                  <TableRow key={service.id}>
                    <TableCell sx={{ fontFamily: ff }}>
                      {service.user?.fullName || 'N/A'}
                    </TableCell>
                    <TableCell sx={{ fontFamily: ff }}>
                      {service.user?.email || 'N/A'}
                    </TableCell>
                    <TableCell>
                      <StatusChip
                        label={SERVICE_TYPE_LABELS[service.serviceType] || service.serviceType || 'N/A'}
                        color={SERVICE_TYPE_COLORS[service.serviceType]?.color || '#718096'}
                        bgColor={SERVICE_TYPE_COLORS[service.serviceType]?.bg || '#F7FAFC'}
                      />
                    </TableCell>
                    <TableCell>
                      <StatusChip
                        label={service.isCompleted ? 'Yes' : 'No'}
                        color={service.isCompleted ? '#22C55E' : '#718096'}
                        bgColor={service.isCompleted ? '#F0FDF4' : '#F7FAFC'}
                      />
                    </TableCell>
                    <TableCell sx={{ fontFamily: ff }}>
                      {formatDateNice(service.createdAt)}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" color="primary" onClick={() => setSelectedService(service)}>
                        <ViewIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          <StyledPagination
            count={totalServices}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </StyledTableContainer>
      )}

      {/* Service Detail Dialog */}
      <Dialog open={!!selectedService} onClose={() => setSelectedService(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontFamily: ff, fontWeight: 600, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Service Details
          <IconButton size="small" onClick={() => setSelectedService(null)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedService && (
            <Box sx={{ fontFamily: ff }}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Box sx={{ fontSize: 12, color: '#718096' }}>Sitter</Box>
                  <Box sx={{ fontSize: 14, fontWeight: 500 }}>{selectedService.user?.fullName || 'N/A'}</Box>
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ fontSize: 12, color: '#718096' }}>Email</Box>
                  <Box sx={{ fontSize: 14 }}>{selectedService.user?.email || 'N/A'}</Box>
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ fontSize: 12, color: '#718096' }}>Phone</Box>
                  <Box sx={{ fontSize: 14 }}>{selectedService.user?.phone || 'N/A'}</Box>
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ fontSize: 12, color: '#718096' }}>Service Type</Box>
                  <StatusChip
                    label={SERVICE_TYPE_LABELS[selectedService.serviceType] || selectedService.serviceType}
                    color={SERVICE_TYPE_COLORS[selectedService.serviceType]?.color || '#718096'}
                    bgColor={SERVICE_TYPE_COLORS[selectedService.serviceType]?.bg || '#F7FAFC'}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ fontSize: 12, color: '#718096' }}>Setup Completed</Box>
                  <StatusChip
                    label={selectedService.isCompleted ? 'Yes' : 'No'}
                    color={selectedService.isCompleted ? '#22C55E' : '#718096'}
                    bgColor={selectedService.isCompleted ? '#F0FDF4' : '#F7FAFC'}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ fontSize: 12, color: '#718096' }}>Created</Box>
                  <Box sx={{ fontSize: 14 }}>{formatDateNice(selectedService.createdAt)}</Box>
                </Grid>
              </Grid>
              {selectedService.settings && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Box sx={{ fontSize: 13, fontWeight: 600, color: '#718096', mb: 1 }}>Settings</Box>
                  <Box sx={{ fontSize: 12, color: '#4A5568', background: '#F7FAFC', p: 2, borderRadius: '8px', whiteSpace: 'pre-wrap' }}>
                    {JSON.stringify(selectedService.settings, null, 2)}
                  </Box>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setSelectedService(null)} sx={{ fontFamily: ff, textTransform: 'none' }}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
